import React, { useEffect, useState } from 'react';
import api from '../../lib/axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Loader2, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { formatDeNumber } from '@/lib/utils';

const STATUS = {
  geplant: 'Geplant',
  ausgefuehrt: 'Als Überweisung übernommen',
  hinterlegt: 'Hinterlegt',
  fehlgeschlagen: 'Fehlgeschlagen',
  zurueckgezogen: 'Zurückgezogen',
  abgelehnt: 'Abgelehnt',
};

const ART = {
  sepa_ueberweisung: 'Überweisung',
  lohnabrechnung: 'Lohnabrechnung',
  csv: 'CSV',
  pdf: 'PDF',
  lastschrift: 'Lastschrift',
};

function dateiAdresse(id) {
  const basis = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  return `${basis}/api/v1/payroll/steuerberater/${encodeURIComponent(id)}/datei`;
}

function geld(wert) {
  if (wert == null || wert === '') return '';
  return `${formatDeNumber(wert)} €`;
}

function zeit(iso) {
  if (!iso) return '';
  const datum = new Date(iso);
  if (Number.isNaN(datum.getTime())) return iso;
  return datum.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function SteuerberaterUpload() {
  const [rows, setRows] = useState([]);
  const [file, setFile] = useState(null);
  const [when, setWhen] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [abruf, setAbruf] = useState({ email: '', day: 25, enabled: false });
  const [abrufBusy, setAbrufBusy] = useState(false);

  const load = () => {
    api.get('/payroll/steuerberater')
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    api.get('/payroll/steuerberater/abruf')
      .then((r) => {
        const daten = r.data || {};
        if (Array.isArray(daten)) return;
        setAbruf({
          email: daten.email || '',
          day: daten.day || 25,
          enabled: Boolean(daten.enabled),
        });
      })
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const onFile = async (chosen) => {
    setFile(chosen || null);
    setPreview(null);
    if (!chosen) return;
    if (!chosen.name.toLowerCase().endsWith('.xml')) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', chosen);
      const r = await api.post('/payroll/steuerberater/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(r.data);
      if (r.data?.execution_date) setWhen(`${r.data.execution_date}T06:00`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Die XML-Datei konnte nicht gelesen werden');
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (when) fd.append('execute_at', when);
      await api.post('/payroll/steuerberater', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Datei ist zum angegebenen Zeitpunkt vorgemerkt');
      setFile(null);
      setWhen('');
      setPreview(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Die Datei konnte nicht gespeichert werden');
    } finally {
      setBusy(false);
    }
  };

  const loeschen = async (row) => {
    const frage = row.batch_id
      ? `„${row.filename}" löschen? Die vorgemerkte Überweisung bleibt.`
      : `„${row.filename}" löschen?`;
    if (!window.confirm(frage)) return;
    try {
      await api.delete(`/payroll/steuerberater/${row.id}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const speichereAbruf = async () => {
    setAbrufBusy(true);
    try {
      await api.put('/payroll/steuerberater/abruf', {
        email: abruf.email,
        day: Number(abruf.day) || 25,
        enabled: abruf.enabled,
      });
      toast.success('Der monatliche Link ist gespeichert.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setAbrufBusy(false);
    }
  };

  const sendeAbruf = async () => {
    setAbrufBusy(true);
    try {
      await api.post('/payroll/steuerberater/abruf/senden');
      toast.success('Der Link wurde verschickt.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Die E-Mail konnte nicht gesendet werden.');
    } finally {
      setAbrufBusy(false);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="steuerberater-upload">
      <CardContent className="p-4 space-y-3">
        <div>
          <h2 className="text-sm font-bold text-slate-800">Datei vom Steuerberater</h2>
          <p className="text-xs text-slate-500 mt-1">
            PDF, CSV und XML. Eine Überweisungs-XML wird zum angegebenen Zeitpunkt als
            Überweisung übernommen. An die Bank geht sie danach mit der bestehenden Freigabe.
            Die Bank bucht am Datum in der Datei.
          </p>
        </div>
        <div className="rounded-md border border-slate-200 p-3 space-y-2" data-testid="steuerberater-abruf">
          <p className="text-xs font-semibold text-slate-700">Link ans Steuerbüro</p>
          <p className="text-xs text-slate-500">
            Am gewählten Tag jedes Monats geht eine E-Mail mit einem Upload-Link raus.
            Lohnabrechnung und Überweisungs-XML werden dort selbst erkannt.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs text-slate-600">
              E-Mail
              <input
                type="email"
                className="block mt-1 h-9 rounded-md border border-slate-200 px-2 text-sm"
                value={abruf.email}
                onChange={(e) => setAbruf((alt) => ({ ...alt, email: e.target.value }))}
                data-testid="steuerberater-abruf-email"
              />
            </label>
            <label className="text-xs text-slate-600">
              Tag im Monat
              <input
                type="number"
                min="1"
                max="31"
                className="block mt-1 h-9 w-20 rounded-md border border-slate-200 px-2 text-sm"
                value={abruf.day}
                onChange={(e) => setAbruf((alt) => ({ ...alt, day: e.target.value }))}
                data-testid="steuerberater-abruf-day"
              />
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-600 mb-2">
              <input
                type="checkbox"
                checked={abruf.enabled}
                onChange={(e) => setAbruf((alt) => ({ ...alt, enabled: e.target.checked }))}
                data-testid="steuerberater-abruf-enabled"
              />
              Monatlich senden
            </label>
            <Button size="sm" type="button" onClick={speichereAbruf} disabled={abrufBusy} data-testid="steuerberater-abruf-save">
              Speichern
            </Button>
            <Button size="sm" type="button" variant="outline" onClick={sendeAbruf} disabled={abrufBusy} data-testid="steuerberater-abruf-send">
              Link jetzt senden
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-slate-600">
            Datei
            <input
              type="file"
              accept=".pdf,.csv,.xml,application/pdf,text/csv,text/xml,application/xml"
              className="block mt-1 text-sm"
              data-testid="steuerberater-file"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
          </label>
          <label className="text-xs text-slate-600">
            Übernehmen am
            <input
              type="datetime-local"
              className="block mt-1 h-9 rounded-md border border-slate-200 px-2 text-sm"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              data-testid="steuerberater-when"
            />
          </label>
          <Button size="sm" onClick={save} disabled={!file || busy} data-testid="steuerberater-save">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
            Vormerken
          </Button>
        </div>
        {preview?.kind === 'xml' && (
          <p className="text-xs text-slate-600" data-testid="steuerberater-preview">
            {preview.count} Zahlungen · {geld(preview.total)}
            {preview.execution_date ? ` · Bankausführung ${preview.execution_date}` : ''}
            {preview.debtor_name ? ` · ${preview.debtor_name}` : ''}
          </p>
        )}
        {rows.length > 0 && (
          <ul className="divide-y divide-slate-100 text-sm" data-testid="steuerberater-list">
            {rows.map((row) => (
              <li key={row.id} className="flex items-center gap-2 py-2">
                <a
                  className="font-medium truncate text-slate-800 underline-offset-2 hover:underline"
                  href={dateiAdresse(row.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`steuerberater-open-${row.id}`}
                >
                  {row.filename}
                </a>
                <span className="text-xs text-slate-500">{zeit(row.execute_at)}</span>
                <span className="text-xs text-slate-500">{ART[row.art] || STATUS[row.status] || row.status}</span>
                {row.matched_name ? <span className="text-xs text-slate-500">{row.matched_name}</span> : null}
                {row.total ? <span className="text-xs text-slate-500">{geld(row.total)}</span> : null}
                {row.error ? <span className="text-xs text-rose-600">{row.error}</span> : null}
                <button type="button" className="ml-auto text-slate-400 hover:text-rose-600"
                        onClick={() => loeschen(row)} data-testid={`steuerberater-delete-${row.id}`}
                        aria-label="Löschen" title="Löschen">
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
